import { supabase, TABLES } from '@/lib/supabase';

export interface SwipeResult {
  success: boolean;
  isMatch?: boolean;
  matchId?: string;
}

export interface ClearLeftSwipesResult {
  success: boolean;
  deletedCount: number;
}

export const swipeUser = async (
  fromUserId: string,
  toUserId: string,
  direction: 'left' | 'right'
): Promise<SwipeResult> => {
  try {
    const { error: swipeError } = await supabase
      .from(TABLES.SWIPES)
      .insert({
        from_user_id: fromUserId,
        to_user_id: toUserId,
        direction,
      });

    if (swipeError) {
      console.error('Swipe error:', swipeError);
      return { success: false };
    }

    // If it's a right swipe, check if the trigger created a match
    if (direction === 'right') {
      const { data: existingMatch, error: matchCheckError } = await supabase
        .from(TABLES.MATCHES)
        .select('id')
        .or(`and(user1_id.eq.${fromUserId},user2_id.eq.${toUserId}),and(user1_id.eq.${toUserId},user2_id.eq.${fromUserId})`)
        .is('unmatched_at', null)
        .maybeSingle();

      if (matchCheckError) {
        console.error('Match check error:', matchCheckError);
        return { success: true, isMatch: false };
      }

      if (existingMatch) {
        return { success: true, isMatch: true, matchId: existingMatch.id };
      }
    }

    return { success: true, isMatch: false };
  } catch (error) {
    console.error('Swipe operation error:', error);
    return { success: false };
  }
};

/**
 * Request to Match — profile-initiated right swipes with an identified
 * accept/decline notification. All writes go through SECURITY DEFINER RPCs
 * (see migration 20260807000000_request_to_match.sql): the requester's old
 * left swipe must be replaced atomically, and the recipient's answer needs to
 * bypass RLS (you can't read swipes made at you).
 */

export type RequestMatchStatus =
  | 'requested'
  | 'matched'
  | 'already_requested'
  | 'already_matched'
  | 'error';

export interface RequestMatchResult {
  status: RequestMatchStatus;
  matchId?: string;
}

/** Send a match request to (i.e. right-swipe) a user from their profile. */
export const requestMatch = async (
  targetUserId: string
): Promise<RequestMatchResult> => {
  const { data, error } = await supabase.rpc('request_match', {
    p_target_user_id: targetUserId,
  });
  if (error) {
    console.error('requestMatch error:', error.message);
    return { status: 'error' };
  }
  const r = (data ?? {}) as { status?: RequestMatchStatus; matchId?: string };
  return { status: r.status ?? 'error', matchId: r.matchId ?? undefined };
};

/** Accept or decline an incoming match request (recipient only). */
export const respondToMatchRequest = async (
  requesterId: string,
  accept: boolean
): Promise<{ ok: boolean; matchId?: string; error?: string }> => {
  const { data, error } = await supabase.rpc('respond_to_match_request', {
    p_requester_id: requesterId,
    p_accept: accept,
  });
  if (error) {
    console.error('respondToMatchRequest error:', error.message);
    return { ok: false, error: error.message };
  }
  const r = (data ?? {}) as { matchId?: string };
  return { ok: true, matchId: r.matchId ?? undefined };
};

/** User ids whose match requests to me are still answerable. */
export const getPendingMatchRequesterIds = async (): Promise<Set<string>> => {
  const { data, error } = await supabase.rpc('my_pending_match_requests');
  if (error || !data) {
    if (error) console.error('getPendingMatchRequesterIds error:', error.message);
    return new Set();
  }
  return new Set(
    (data as { requester_id: string }[]).map((r) => r.requester_id)
  );
};

/** The viewer's standing swipe/match toward another user, for button state. */
export interface RelationToUser {
  swipedDirection: 'left' | 'right' | null;
  matchId: string | null;
}

export const getRelationToUser = async (
  viewerId: string,
  targetUserId: string
): Promise<RelationToUser> => {
  const [swipeRes, matchRes] = await Promise.all([
    supabase
      .from(TABLES.SWIPES)
      .select('direction')
      .eq('from_user_id', viewerId)
      .eq('to_user_id', targetUserId)
      .maybeSingle(),
    supabase
      .from(TABLES.MATCHES)
      .select('id')
      .or(
        `and(user1_id.eq.${viewerId},user2_id.eq.${targetUserId}),and(user1_id.eq.${targetUserId},user2_id.eq.${viewerId})`
      )
      .is('unmatched_at', null)
      .maybeSingle(),
  ]);
  if (swipeRes.error) console.error('getRelationToUser swipe error:', swipeRes.error.message);
  if (matchRes.error) console.error('getRelationToUser match error:', matchRes.error.message);
  return {
    swipedDirection:
      (swipeRes.data?.direction as 'left' | 'right' | undefined) ?? null,
    matchId: matchRes.data?.id ?? null,
  };
};

export const clearLeftSwipes = async (userId: string): Promise<ClearLeftSwipesResult> => {
  try {
    const { data, error } = await supabase
      .from(TABLES.SWIPES)
      .delete()
      .eq('from_user_id', userId)
      .eq('direction', 'left')
      .select('id');

    if (error) {
      console.error('Clear left swipes error:', error);
      return { success: false, deletedCount: 0 };
    }

    return { success: true, deletedCount: data?.length ?? 0 };
  } catch (error) {
    console.error('Clear left swipes unexpected error:', error);
    return { success: false, deletedCount: 0 };
  }
};
